import { useEffect } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { Bell, LogOut, User as UserIcon, Wallet, Megaphone } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole, type AppRole } from "@/hooks/use-user-role";
import { resolveAvatarUrl } from "@/lib/avatar";
import { useNotifications } from "@/hooks/use-notifications";

const ROLE_LABEL: Record<AppRole, string> = {
  superadmin: "Super Admin",
  admin: "Administrator",
  bendahara: "Bendahara",
  guru: "Guru",
};

function greetingWIB() {
  // WIB = UTC+7
  const h = Number(
    new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      hour12: false,
      timeZone: "Asia/Jakarta",
    }).format(new Date())
  );
  if (h >= 4 && h < 11) return "Selamat Pagi,";
  if (h >= 11 && h < 15) return "Selamat Siang,";
  if (h >= 15 && h < 18) return "Selamat Sore,";
  return "Selamat Malam,";
}


export function MobileTopBar() {
  const navigate = useNavigate();
  const { role } = useUserRole();
  const [name, setName] = useCachedState<string>("topbar:name", "");
  const [email, setEmail] = useCachedState<string>("topbar:email", "");
  const [avatar, setAvatar] = useCachedState<string | null>("topbar:avatar", null);
  const [gender, setGender] = useCachedState<"L" | "P" | null>("topbar:gender", null);

  useEffect(() => {
    let cancelled = false;
    const loadProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) {
        if (!user && !cancelled) {
          setName("");
          setEmail("");
          setAvatar(null);
          setGender(null);
        }
        return;
      }
      const { data: prof } = await supabase
        .from("profiles").select("full_name, avatar_url, email, gender").eq("id", user.id).maybeSingle();
      const url = await resolveAvatarUrl(prof?.avatar_url);
      if (cancelled) return;
      setName(prof?.full_name || user.email?.split("@")[0] || "Pengguna");
      setEmail(prof?.email || user.email || "");
      setAvatar(url ?? null);
      setGender((prof as { gender?: "L" | "P" | null } | null)?.gender ?? null);
    };
    void loadProfile();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        setName("");
        setEmail("");
        setAvatar(null);
        setGender(null);
        return;
      }
      if (event === "SIGNED_IN" || event === "USER_UPDATED") void loadProfile();
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const logout = async () => {
    try { await supabase.rpc("mark_offline"); } catch { /* ignore */ }
    window.dispatchEvent(new Event("almunajah:account-reset"));
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  };

  const initials = name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "U";
  const roleLabel = role ? ROLE_LABEL[role] : "Guru";
  const honorific = gender === "P" ? "Ibu" : "Bapak";


  return (
    <header
      className="md:hidden relative sticky top-0 z-30 text-primary-foreground"
      style={{ paddingTop: "env(safe-area-inset-top)", marginBottom: "-1px" }}
    >
      {/* Gradient body */}
      <div
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, oklch(0.32 0.08 160) 0%, oklch(0.42 0.13 162) 55%, oklch(0.52 0.16 165) 100%)",
          paddingBottom: "56px",
        }}
      >
        {/* Mosque silhouette decoration */}
        <div
          className="absolute right-0 top-0 bottom-0 w-3/5 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(ellipse 60% 80% at 70% 95%, rgba(255,255,255,0.5) 0%, transparent 55%), radial-gradient(circle at 55% 80%, rgba(255,255,255,0.4) 0%, transparent 18%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.4) 0%, transparent 14%)",
          }}
        />
        <div className="absolute -right-10 -top-12 w-40 h-40 rounded-full bg-white/10 blur-2xl pointer-events-none" />

        {/* Content */}
        <div className="relative px-5 pt-3 pb-1 flex items-start gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="relative shrink-0 rounded-full active:scale-95 transition-transform mt-0.5">
                <Avatar className="w-11 h-11 border-2 border-white shadow-lg">
                  {avatar && <AvatarImage src={avatar} />}
                  <AvatarFallback className="bg-white text-primary font-bold text-sm">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-white" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 p-0 overflow-hidden">
              <div
                className="px-4 pt-4 pb-3 text-white"
                style={{
                  background:
                    "linear-gradient(135deg, oklch(0.32 0.08 160) 0%, oklch(0.42 0.13 162) 60%, oklch(0.52 0.16 165) 100%)",
                }}
              >
                <div className="flex items-center gap-3">
                  <Avatar className="w-14 h-14 border-2 border-white shadow-md">
                    {avatar && <AvatarImage src={avatar} />}
                    <AvatarFallback className="bg-white text-primary font-bold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-[15px] leading-tight truncate">
                      {honorific} {name || "—"}
                    </div>
                    <div className="text-[11px] opacity-90 truncate mt-0.5">{email}</div>
                    <div className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm text-[10px] font-semibold border border-white/25">
                      <UserIcon className="w-2.5 h-2.5" />
                      {roleLabel}
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-3 py-2 text-[12px] space-y-1.5">
                <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-muted/50">
                  <span className="text-muted-foreground">Jenis Kelamin</span>
                  <span className="font-medium">{gender === "P" ? "Perempuan" : gender === "L" ? "Laki-laki" : "—"}</span>
                </div>
                <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-muted/50">
                  <span className="text-muted-foreground">Peran</span>
                  <span className="font-semibold text-primary">{roleLabel}</span>
                </div>
                <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-muted/50">
                  <span className="text-muted-foreground">Status</span>
                  <span className="inline-flex items-center gap-1 font-medium text-emerald-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Aktif
                  </span>
                </div>
              </div>
              <DropdownMenuSeparator className="my-0" />
              <DropdownMenuItem
                onClick={logout}
                className="m-1 rounded-md text-destructive focus:text-destructive focus:bg-destructive/10 font-medium justify-center"
              >
                <LogOut className="w-4 h-4 mr-2" /> Keluar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="min-w-0 flex-1 pt-0">
            <div className="text-[11px] opacity-90 leading-tight">{greetingWIB()}</div>
            <div className="font-bold text-[14px] leading-tight truncate mt-0.5">
              {honorific} {name || "—"}
            </div>
            <div className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-white/15 backdrop-blur-sm text-[10px] font-medium border border-white/20">
              <UserIcon className="w-2.5 h-2.5" />
              {roleLabel}
            </div>
          </div>

          <NotifBell />

        </div>

        {/* Curve — solid background fill, no shadow/ridge so it merges seamlessly with the page */}
        <div className="absolute left-0 right-0 bottom-0 pointer-events-none" style={{ height: "60px" }}>
          <svg
            className="absolute inset-x-0 bottom-0 w-full text-background"
            style={{ height: "61px", transform: "translateY(1px)" }}
            viewBox="0 0 400 60"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M 0 60 L 0 26 C 70 25 120 52 210 47 C 290 42 340 20 400 24 L 400 60 Z"
              fill="currentColor"
            />
          </svg>
        </div>



      </div>
    </header>
  );
}

function NotifBell() {
  const navigate = useNavigate();
  const { items, count, isFinance, markAllRead } = useNotifications();
  const title = isFinance ? "Pembayaran perlu dikonfirmasi" : "Pengumuman terbaru";
  const Icon = isFinance ? Wallet : Megaphone;
  const fmt = (d: string) =>
    new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <DropdownMenu onOpenChange={(open) => { if (open) markAllRead(); }}>

      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Notifikasi"
          className="relative shrink-0 mt-0.5 w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 flex items-center justify-center text-white active:scale-95 transition-transform shadow-md"
        >
          <Bell className="w-4 h-4" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold flex items-center justify-center border border-white animate-pulse">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-[70vh] overflow-y-auto">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span className="font-semibold">{title}</span>
          {count > 0 && (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
              {count} baru
            </span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            Tidak ada notifikasi
          </div>
        ) : (
          items.slice(0, 10).map((n) => (
            <DropdownMenuItem
              key={n.id}
              className="flex items-start gap-2.5 py-2.5 cursor-pointer"
              onClick={() => navigate({ to: n.url })}
            >
              <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                <Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[13px] leading-tight truncate">{n.title}</div>
                {n.subtitle && (
                  <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{n.subtitle}</div>
                )}
                <div className="text-[10px] text-muted-foreground/80 mt-0.5">{fmt(n.date)}</div>
              </div>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

